import axiosClient from '@/api/axios/axiosClient';
import type { ApiResponse } from '@/types/api';
import { UpdatedUserProfileSchema } from '@/types/api/response/user';
import { UpdateUserProfilePayloadSchema, type UpdateUserProfilePayload } from '@/types/api/payload/user';
import useAppStore from '@/stores/appStore';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useDelete } from '@/hooks/useQuery';
import { useQueryClient, keepPreviousData, useMutation as useReactMutation } from '@tanstack/react-query';
import type {
  BotActivityEventType,
  UserDashboard,
  UserProfile,
  UserActivityData,
  UserFollowsData,
} from '@/types/api/response/user';
import type { FollowUserPayload } from '@/types/api/payload/user';

const BASE_USER_URL = '/user';

/**
 * (GET) Hook to fetch the current user's profile
 * Endpoint: GET /public/users/{id} (authenticated self)
 *
 * @param options.enabled - Skip the request entirely when false. Needed on
 *   pages that render for anonymous visitors too (e.g. a public user profile,
 *   where this only serves to answer "is the visitor the target?"): firing it
 *   without a token would 401 and take the session-expiry path for nothing.
 */
export function useUserProfile(options?: { enabled?: boolean }) {
  const userId = useAppStore((state) => state.backendUserId);
  const { isAuthenticated, isLoading } = useAuth0();
  return useQuery<UserProfile>(
    `/public/users/${encodeURIComponent(userId ?? '')}`,
    ['userProfile', userId],
    {
      staleTime: 1000 * 60 * 5,
      enabled: (options?.enabled ?? true) && isAuthenticated && !isLoading && !!userId,
    },
  );
}

/**
 * (GET) Fetch the authenticated user's dashboard summary.
 * Endpoint: GET /user/dashboard
 */
export function useUserDashboard(options?: { enabled?: boolean }) {
  return useQuery<UserDashboard>(
    `${BASE_USER_URL}/dashboard`,
    ['user-dashboard'],
    {
      enabled: options?.enabled ?? true,
      staleTime: 1000 * 30,
    },
  );
}

/**
 * (GET) The people the authenticated user follows.
 * Endpoint: GET /user/following
 *
 * Every row here is by definition someone the caller follows — the endpoint has
 * no `is_following` field, so this list membership IS the follow state.
 */
export function useFollowing(params?: {
  cursor?: string;
  limit?: number;
  /** Skip the request entirely — used when the other direction is on screen. */
  enabled?: boolean;
}) {
  const queryParams = new URLSearchParams();
  if (params?.cursor) {
    queryParams.set('cursor', params.cursor);
  }
  if (params?.limit) {
    queryParams.set('limit', String(params.limit));
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${BASE_USER_URL}/following?${queryString}`
    : `${BASE_USER_URL}/following`;

  return useQuery<UserFollowsData>(
    endpoint,
    ['user-following', params?.cursor, params?.limit],
    {
      enabled: params?.enabled ?? true,
      staleTime: 1000 * 30,
      placeholderData: keepPreviousData,
    },
  );
}

/**
 * (GET) Activity merged across public strategies owned by people the
 * authenticated user follows.
 * Endpoint: GET /user/following/activity
 *
 * The endpoint is cursor-paginated and its only server-side filter is the
 * repeatable `event_type` query parameter. Followed private users do not
 * contribute rows, and owner-only financial fields are redacted by the API.
 */
export function useFollowingActivity(params?: {
  cursor?: string;
  limit?: number;
  event_type?: BotActivityEventType[];
  enabled?: boolean;
}) {
  const queryParams = new URLSearchParams();
  if (params?.cursor) {
    queryParams.set('cursor', params.cursor);
  }
  if (params?.limit) {
    queryParams.set('limit', String(params.limit));
  }
  params?.event_type?.forEach((eventType) => {
    queryParams.append('event_type', eventType);
  });

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${BASE_USER_URL}/following/activity?${queryString}`
    : `${BASE_USER_URL}/following/activity`;

  return useQuery<UserActivityData>(
    endpoint,
    [
      'user-following-activity',
      params?.cursor,
      params?.limit,
      params?.event_type,
    ],
    {
      enabled: params?.enabled ?? true,
      staleTime: 1000 * 30,
      placeholderData: keepPreviousData,
    },
  );
}

/**
 * (GET) The people who follow the authenticated user.
 * Endpoint: GET /user/followers
 *
 * Same row shape as `useFollowing`, but membership here says nothing about
 * whether the caller follows back — the API exposes no such flag.
 */
export function useFollowers(params?: {
  cursor?: string;
  limit?: number;
  /** Skip the request entirely — used when the other direction is on screen. */
  enabled?: boolean;
}) {
  const queryParams = new URLSearchParams();
  if (params?.cursor) {
    queryParams.set('cursor', params.cursor);
  }
  if (params?.limit) {
    queryParams.set('limit', String(params.limit));
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${BASE_USER_URL}/followers?${queryString}`
    : `${BASE_USER_URL}/followers`;

  return useQuery<UserFollowsData>(
    endpoint,
    ['user-followers', params?.cursor, params?.limit],
    {
      enabled: params?.enabled ?? true,
      staleTime: 1000 * 30,
      placeholderData: keepPreviousData,
    },
  );
}

/**
 * (DELETE) Unfollow a user. Pass the target's id as the mutation variable.
 * Endpoint: DELETE /user/follow/{userId}
 */
export function useUnfollowUser() {
  const queryClient = useQueryClient();
  return useDelete(`${BASE_USER_URL}/follow`, {
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['user-following'] });
      queryClient.invalidateQueries({ queryKey: ['user-followers'] });
      queryClient.invalidateQueries({ queryKey: ['user-following-activity'] });
      // Without this the profile page would keep reporting "Following" after a
      // successful unfollow.
      queryClient.invalidateQueries({ queryKey: ['public-user', userId] });
    },
  });
}

/**
 * (POST) Follow a user.
 * Endpoint: POST /user/follow
 */
export function useFollowUser() {
  const queryClient = useQueryClient();
  return useMutation<unknown, FollowUserPayload>(`${BASE_USER_URL}/follow`, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-following'] });
      queryClient.invalidateQueries({ queryKey: ['user-followers'] });
      queryClient.invalidateQueries({ queryKey: ['user-following-activity'] });
      // The profile payload carries is_following, so the profile page's button
      // updates from a refetch of this key.
      queryClient.invalidateQueries({ queryKey: ['public-user', variables.user_id] });
    },
  });
}


/** Save profile changes, then refresh all views that display this account. */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  return useReactMutation({
    mutationFn: async (payload: UpdateUserProfilePayload) => {
      const response = await axiosClient.patch<ApiResponse<unknown>>(
        '/user/profile',
        UpdateUserProfilePayloadSchema.parse(payload),
      );
      return UpdatedUserProfileSchema.parse(response.data.data);
    },
    onSuccess: async (profile) => {
      // Cancel stale reads before replacing the cache with the saved account.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['userProfile', profile.id] }),
        queryClient.cancelQueries({ queryKey: ['public-user', profile.id] }),
      ]);
      // A save may finish after logout or after another account signs in.
      // Never restore that old account's profile or user state in that case.
      const { backendUserId, session } = useAppStore.getState();
      if (backendUserId !== profile.id || session.isLoggingOut) return;
      const data = { ...profile, is_following: false };
      const response = { code: 0, status: 200, data };
      queryClient.setQueryData(['userProfile', profile.id], response);
      // Public profile queries can have additional auth-related key segments.
      queryClient.setQueriesData({ queryKey: ['public-user', profile.id] }, response);
      useAppStore.getState().setUser({ username: profile.username, name: profile.name, image: profile.image });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['public-user', profile.id] }),
        queryClient.invalidateQueries({ queryKey: ['user-following'] }),
        queryClient.invalidateQueries({ queryKey: ['user-followers'] }),
      ]);
    },
  });
}
